import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Copy } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { TimeclockShift, TimeclockShiftKind } from '@aeris/contracts'
import {
  Badge,
  Button,
  CheckboxField,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  QueryState,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  notify,
} from '@aeris/ui'

import {
  ApiError,
  copyTimeclockSchedule,
  createTimeclockShift,
  fetchBranches,
  fetchTimeclockSchedule,
  fetchTimeclockShifts,
  saveTimeclockSchedule,
  updateTimeclockShift,
} from '../lib/api.ts'
import { dayText } from '../lib/ponto-sheet.ts'

/**
 * A escala (F341): o gerente monta a semana de cada loja — cada pessoa numa
 * linha, cada dia numa coluna, o turno em cada célula — e ela pode mudar de
 * semana para semana. "Copiar a semana passada" só preenche o que está vazio.
 */

const messageOf = (cause: unknown, fallback: string): string =>
  cause instanceof ApiError ? cause.message : fallback

const addDays = (day: string, n: number): string => {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, (d ?? 1) + n))
    .toISOString()
    .slice(0, 10)
}

const today = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(
    new Date()
  )

const shiftLabel = (shift: TimeclockShift): string =>
  shift.kind === 'folga'
    ? shift.name
    : `${shift.name} · ${shift.startsAt ?? ''}–${shift.endsAt ?? ''}`

const EMPTY_FORM = {
  name: '',
  kind: 'trabalho' as TimeclockShiftKind,
  startsAt: '08:00',
  endsAt: '18:00',
  breakMinutes: '60',
}

const ShiftsDialog = ({ onClose }: { onClose: () => void }): ReactNode => {
  const queryClient = useQueryClient()
  const shifts = useQuery({
    queryKey: ['timeclock', 'shifts'],
    queryFn: async ({ signal }) => await fetchTimeclockShifts(signal),
  })
  const [editing, setEditing] = useState<TimeclockShift | null>(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [archived, setArchived] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const edit = (shift: TimeclockShift | null) => {
    setEditing(shift)
    setError(null)
    setArchived(shift?.archived ?? false)
    setForm(
      shift === null
        ? EMPTY_FORM
        : {
            name: shift.name,
            kind: shift.kind,
            startsAt: shift.startsAt ?? '08:00',
            endsAt: shift.endsAt ?? '18:00',
            breakMinutes: String(shift.breakMinutes),
          }
    )
  }

  const save = useMutation({
    mutationFn: async () => {
      const work = form.kind === 'trabalho'
      const body = {
        name: form.name,
        kind: form.kind,
        startsAt: work ? form.startsAt : null,
        endsAt: work ? form.endsAt : null,
        breakMinutes: work ? Number(form.breakMinutes) : 0,
      }
      return editing === null
        ? await createTimeclockShift(body)
        : await updateTimeclockShift(editing.id, {
            ...body,
            archived,
            version: editing.version,
          })
    },
    onSuccess: async () => {
      notify.success('Turno salvo.')
      edit(null)
      await queryClient.invalidateQueries({ queryKey: ['timeclock'] })
    },
    onError: async (cause: unknown) => {
      // Alguém mudou o turno antes: volta pra lista, recarregada, com o aviso.
      if (cause instanceof ApiError && cause.code === 'stale') {
        edit(null)
        await queryClient.invalidateQueries({ queryKey: ['timeclock'] })
      }
      setError(messageOf(cause, 'Não foi possível salvar o turno.'))
    },
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title="Turnos"
        description="Os horários prontos que a escala usa. O fim antes do começo é um turno que passa da meia-noite."
      >
        <div className="flex flex-col gap-4">
          <ul className="flex flex-wrap gap-2">
            {(shifts.data?.shifts ?? []).map((shift) => (
              <li key={shift.id}>
                <Button
                  size="sm"
                  variant={editing?.id === shift.id ? 'primary' : 'secondary'}
                  onClick={() => {
                    edit(shift)
                  }}
                >
                  {shiftLabel(shift)}
                  {shift.archived ? ' (arquivado)' : ''}
                </Button>
              </li>
            ))}
          </ul>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault()
              save.mutate()
            }}
          >
            <p className="text-(length:--text-sm) font-(--weight-medium)">
              {editing === null ? 'Novo turno' : `Editar ${editing.name}`}
            </p>
            {error === null ? null : (
              <InlineAlert tone="danger">{error}</InlineAlert>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nome" required>
                {(field) => (
                  <Input
                    {...field}
                    value={form.name}
                    maxLength={40}
                    placeholder="Comercial"
                    onChange={(event) => {
                      setForm({ ...form, name: event.target.value })
                    }}
                  />
                )}
              </Field>
              <Field label="Tipo">
                {(field) => (
                  <Select
                    {...field}
                    value={form.kind}
                    onChange={(event) => {
                      setForm({
                        ...form,
                        kind: event.target.value as TimeclockShiftKind,
                      })
                    }}
                  >
                    <option value="trabalho">Trabalho</option>
                    <option value="folga">Folga</option>
                  </Select>
                )}
              </Field>
              {form.kind === 'trabalho' ? (
                <>
                  <Field label="Começa">
                    {(field) => (
                      <Input
                        {...field}
                        type="time"
                        value={form.startsAt}
                        onChange={(event) => {
                          setForm({ ...form, startsAt: event.target.value })
                        }}
                      />
                    )}
                  </Field>
                  <Field label="Termina">
                    {(field) => (
                      <Input
                        {...field}
                        type="time"
                        value={form.endsAt}
                        onChange={(event) => {
                          setForm({ ...form, endsAt: event.target.value })
                        }}
                      />
                    )}
                  </Field>
                  <Field
                    label="Intervalo (minutos)"
                    hint="O almoço. De 0 a 240."
                  >
                    {(field) => (
                      <Input
                        {...field}
                        inputMode="numeric"
                        value={form.breakMinutes}
                        onChange={(event) => {
                          setForm({
                            ...form,
                            breakMinutes: event.target.value.replace(
                              /\D/gu,
                              ''
                            ),
                          })
                        }}
                      />
                    )}
                  </Field>
                </>
              ) : null}
            </div>
            {editing === null ? null : (
              <CheckboxField
                id="ponto-turno-arquivado"
                label="Arquivado (some da escala nova; os dias que já usaram ficam)"
                checked={archived}
                onCheckedChange={(checked) => {
                  setArchived(checked === true)
                }}
              />
            )}
            <DialogFooter>
              {editing === null ? (
                <Button type="button" variant="secondary" onClick={onClose}>
                  Fechar
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    edit(null)
                  }}
                >
                  Novo turno
                </Button>
              )}
              <Button
                type="submit"
                variant="primary"
                loading={save.isPending}
                disabled={form.name.trim() === ''}
              >
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export const PontoEscala = ({
  canManage,
}: {
  canManage: boolean
}): ReactNode => {
  const queryClient = useQueryClient()
  const [branchId, setBranchId] = useState('')
  const [week, setWeek] = useState(today)
  // O que o gerente mudou e ainda não salvou: `pessoa:dia` → turno (ou nulo).
  const [draft, setDraft] = useState<Map<string, string | null>>(
    () => new Map()
  )
  const [shiftsOpen, setShiftsOpen] = useState(false)

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: ({ signal }) => fetchBranches(undefined, signal),
  })
  const active = (branches.data?.branches ?? []).filter(
    (branch) => branch.status === 'active'
  )
  const chosen = branchId === '' ? (active[0]?.id ?? '') : branchId

  const shifts = useQuery({
    queryKey: ['timeclock', 'shifts'],
    queryFn: async ({ signal }) => await fetchTimeclockShifts(signal),
  })
  const schedule = useQuery({
    queryKey: ['timeclock', 'schedule', chosen, week],
    queryFn: async ({ signal }) =>
      await fetchTimeclockSchedule(week, chosen, signal),
    enabled: chosen !== '',
  })
  const data = schedule.data
  const usable = (shifts.data?.shifts ?? []).filter((shift) => !shift.archived)
  const byId = new Map((shifts.data?.shifts ?? []).map((s) => [s.id, s]))

  const move = (days: number) => {
    setDraft(new Map())
    setWeek((current) => addDays(data?.week ?? current, days))
  }

  const save = useMutation({
    mutationFn: async () => {
      if (data === undefined) return
      const cells = data.people.flatMap((person) =>
        person.cells.flatMap((cell) => {
          const key = `${person.employeeId}:${cell.day}`
          if (!draft.has(key)) return []
          const shiftId = draft.get(key) ?? null
          return shiftId === cell.shiftId
            ? []
            : [
                {
                  employeeId: person.employeeId,
                  day: cell.day,
                  shiftId,
                  version: cell.version,
                },
              ]
        })
      )
      if (cells.length > 0) await saveTimeclockSchedule({ cells })
    },
    onSuccess: async () => {
      notify.success('Escala salva.')
      setDraft(new Map())
      await queryClient.invalidateQueries({ queryKey: ['timeclock'] })
    },
    onError: async (cause: unknown) => {
      notify.error(messageOf(cause, 'Não foi possível salvar a escala.'))
      if (cause instanceof ApiError && cause.status === 409) {
        setDraft(new Map())
        await queryClient.invalidateQueries({
          queryKey: ['timeclock', 'schedule'],
        })
      }
    },
  })

  const copy = useMutation({
    mutationFn: async () => {
      if (data === undefined) return { copied: 0 }
      return await copyTimeclockSchedule({
        branchId: chosen,
        fromWeek: addDays(data.week, -7),
        toWeek: data.week,
      })
    },
    onSuccess: async (result) => {
      notify.success(
        result.copied === 0
          ? 'Nada para copiar: a semana passada está vazia ou esta já está preenchida.'
          : `${String(result.copied)} dia(s) copiado(s) da semana passada.`
      )
      await queryClient.invalidateQueries({ queryKey: ['timeclock'] })
    },
    onError: (cause: unknown) => {
      notify.error(messageOf(cause, 'Não foi possível copiar.'))
    },
  })

  const dirty = draft.size > 0

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Loja">
          {(field) => (
            <Select
              {...field}
              value={chosen}
              disabled={dirty}
              onChange={(event) => {
                setDraft(new Map())
                setBranchId(event.target.value)
              }}
            >
              {active.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Semana anterior"
            disabled={dirty}
            onClick={() => {
              move(-7)
            }}
          >
            <ChevronLeft aria-hidden="true" className="h-4 w-4" />
          </Button>
          <span className="min-w-36 text-center text-(length:--text-sm) font-(--weight-medium)">
            {data === undefined
              ? '…'
              : `${dayText(data.week)} a ${dayText(addDays(data.week, 6))}`}
          </span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Próxima semana"
            disabled={dirty}
            onClick={() => {
              move(7)
            }}
          >
            <ChevronRight aria-hidden="true" className="h-4 w-4" />
          </Button>
        </div>
        {canManage ? (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              disabled={!dirty}
              loading={save.isPending}
              onClick={() => {
                save.mutate()
              }}
            >
              Salvar a semana
            </Button>
            <Button
              variant="secondary"
              disabled={dirty}
              loading={copy.isPending}
              leadingIcon={<Copy aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                copy.mutate()
              }}
            >
              Copiar a semana passada
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShiftsOpen(true)
              }}
            >
              Turnos
            </Button>
          </div>
        ) : null}
      </div>

      {dirty ? (
        <InlineAlert tone="warning">
          <span className="flex flex-wrap items-center gap-2">
            A semana tem mudanças não salvas. Salve ou descarte antes de trocar
            de semana ou de loja.
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setDraft(new Map())
              }}
            >
              Descartar
            </Button>
          </span>
        </InlineAlert>
      ) : null}

      {canManage && usable.length === 0 && !shifts.isPending ? (
        <InlineAlert tone="info">
          Crie os turnos primeiro (por exemplo “Comercial 08–18” e “Folga”) em
          Turnos.
        </InlineAlert>
      ) : null}

      <QueryState
        // A consulta desligada (sem loja) fica `isPending` pra sempre: as
        // lojas decidem antes.
        isPending={branches.isPending || (chosen !== '' && schedule.isPending)}
        isError={branches.isError || schedule.isError}
        error={branches.error ?? schedule.error}
        onRetry={() => {
          void branches.refetch()
          if (chosen !== '') void schedule.refetch()
        }}
        what="a escala"
        isEmpty={chosen === '' || data?.people.length === 0}
        empty={
          chosen === '' ? (
            <EmptyState
              title="Nenhuma loja ativa"
              description="A escala é por loja. Cadastre uma loja primeiro."
            />
          ) : (
            <EmptyState
              title="Ninguém no ponto nesta loja"
              description="A escala mostra quem está cadastrado no ponto em Funcionários."
            />
          )
        }
      >
        {data === undefined ? null : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Funcionário</TableHeaderCell>
                  {data.days.map((day) => (
                    <TableHeaderCell key={day}>{dayText(day)}</TableHeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.people.map((person) => (
                  <TableRow key={person.employeeId}>
                    <TableCell className="font-(--weight-medium)">
                      {person.name}
                    </TableCell>
                    {person.cells.map((cell) => {
                      const key = `${person.employeeId}:${cell.day}`
                      const value = draft.has(key)
                        ? (draft.get(key) ?? null)
                        : cell.shiftId
                      const current =
                        value === null ? undefined : byId.get(value)
                      return (
                        <TableCell key={cell.day}>
                          {canManage ? (
                            <Select
                              aria-label={`${person.name}, ${dayText(cell.day)}`}
                              value={value ?? ''}
                              onChange={(event) => {
                                const next = new Map(draft)
                                next.set(
                                  key,
                                  event.target.value === ''
                                    ? null
                                    : event.target.value
                                )
                                setDraft(next)
                              }}
                            >
                              <option value="">—</option>
                              {current?.archived === true ? (
                                <option value={current.id}>
                                  {current.name} (arquivado)
                                </option>
                              ) : null}
                              {usable.map((shift) => (
                                <option key={shift.id} value={shift.id}>
                                  {shiftLabel(shift)}
                                </option>
                              ))}
                            </Select>
                          ) : current === undefined ? (
                            '—'
                          ) : (
                            <Badge
                              tone={
                                current.kind === 'folga' ? 'neutral' : 'info'
                              }
                            >
                              {shiftLabel(current)}
                            </Badge>
                          )}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </QueryState>

      {shiftsOpen ? (
        <ShiftsDialog
          onClose={() => {
            setShiftsOpen(false)
          }}
        />
      ) : null}
    </div>
  )
}
